"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { SpeakerSimpleHighIcon, SpeakerSimpleXIcon } from "@phosphor-icons/react/ssr";

import VideoPlayer from "~/componentsDS/VideoPlayer";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useLogEvent } from "~/hooks/useLogEvent";
import { useTrackEvent } from "~/hooks/useTrackEvent";

const SOURCE_BASE = "https://res.cloudinary.com/dirscuuhp/video/upload/v1789996704/new_design/updated_no_outro_bgm_lxcwtr";
const MP4_URL = `${SOURCE_BASE}.mp4`;
const HLS_URL = `${SOURCE_BASE}.m3u8`;
const POSTER_URL = "https://res.cloudinary.com/dirscuuhp/video/upload/so_0/v1789996704/new_design/updated_no_outro_bgm_lxcwtr.webp";
const VIDEO_ID = "bodymatched_media_card_video";

export default function MediaCard() {
    const sendEvent = useTrackEvent();
    const logEvent = useLogEvent();
    const cardRef = useRef<HTMLElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isMuted, setMuted] = useState(true);
    const [shouldLoadPoster, setShouldLoadPoster] = useState(false);

    useEffect(() => {
        const card = cardRef.current;
        if (!card) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry?.isIntersecting) return;
                setShouldLoadPoster(true);
                observer.disconnect();
            },
            { rootMargin: "800px 0px" }
        );

        observer.observe(card);

        return () => observer.disconnect();
    }, []);

    const toggleSound = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        video.muted = !video.muted;
        setMuted(video.muted);

        if (video.paused) {
            video.play().catch((error) => {
                logEvent({
                    level: "warn",
                    message: "BodyMatched media video play() failed",
                    videoId: VIDEO_ID,
                    error: String(error),
                });
            });
        }

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "clipboard_video",
                meta_data: {
                    click_text: video.muted ? "mute" : "unmute",
                    product_name: "anti-aging face cream",
                    video_id: VIDEO_ID,
                },
            },
        });
    }, [logEvent, sendEvent]);

    return (
        <article ref={cardRef} className="relative h-full w-full overflow-hidden rounded-2xl bg-[#f4f5f7]">
            <VideoPlayer
                ref={videoRef}
                autoPlay
                muted={isMuted}
                loop
                playsInline
                preload="none"
                preferHls
                mp4={MP4_URL}
                hls={HLS_URL}
                poster={shouldLoadPoster ? POSTER_URL : undefined}
                onClick={toggleSound}
                className="absolute inset-0 h-full w-full cursor-pointer object-cover"
            />

            <button
                type="button"
                aria-label={isMuted ? "Unmute video" : "Mute video"}
                onClick={toggleSound}
                className="absolute bottom-4 right-4 z-10 flex size-10 items-center justify-center rounded-full bg-trueBlack/40 text-pureWhite backdrop-blur-[10px] lg:bottom-6 lg:right-6"
            >
                {isMuted ? <SpeakerSimpleXIcon size={18} weight="fill" aria-hidden="true" /> : <SpeakerSimpleHighIcon size={18} weight="fill" aria-hidden="true" />}
            </button>
        </article>
    );
}
